import React from 'react';
import { Document } from '@contentful/rich-text-types';

import { formatToPhone } from '../../../utils/formatPhoneNumber';

// Form Fields
import { TextInput } from '../../FormFields/TextInput';
import { DateInput } from '../../FormFields/Date';
import { TextAreaInput } from '../../FormFields/TextAreaInput';
import { Select } from '../../FormFields/Select';
import { Checkbox } from '../../FormFields/Checkbox';

import { TextInputType, InputAutocomplete } from '../../FormFields/shared';
import { formFieldEnum } from './shared';

interface FormFieldProps {
  id: string;
  type: formFieldEnum;
  label: string;
  placeholder?: string;
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  autocompleteAttribute?: InputAutocomplete;
  options?: any[];
  optionType?: string;
  disclaimer?: Document | null;
  formGroupId?: string;
  state: any;
  errors: any;
  variant?: 'default' | 'filterbar' | 'gl4c';
  handleOnChange: (key: string, val: any) => void;
}

export function FormField({
  id,
  type,
  formGroupId,
  label,
  placeholder,
  required,
  minLength,
  maxLength,
  autocompleteAttribute,
  state,
  errors,
  handleOnChange,
  variant,
  ...fields
}: FormFieldProps) {
  switch (type) {
    case formFieldEnum.TEXT:
    case formFieldEnum.EMAIL:
    case formFieldEnum.NUMBER:
      let inputType = TextInputType.TEXT;

      if (type === formFieldEnum.EMAIL) {
        inputType = TextInputType.EMAIL;
      } else if (type === formFieldEnum.NUMBER) {
        inputType = TextInputType.NUMBER;
      }

      return (
        <TextInput
          type={inputType}
          label={label}
          placeholder={placeholder}
          value={state[id]}
          error={errors[id]}
          required={required}
          minLength={minLength}
          maxLength={maxLength}
          autoComplete={autocompleteAttribute}
          handleOnChange={(e, val: string) => handleOnChange(id, val)}
        />
      );

    case formFieldEnum.PHONE:
      return (
        <TextInput
          type={TextInputType.PHONE}
          label={label}
          placeholder={placeholder}
          value={state[id]}
          error={errors[id]}
          required={required}
          inputMode="numeric"
          pattern="[0-9]*"
          minLength={minLength}
          maxLength={14}
          autoComplete={autocompleteAttribute ?? 'tel'}
          handleOnChange={(e, val: string) =>
            handleOnChange(id, formatToPhone(val) ?? '')
          }
        />
      );

    case formFieldEnum.DATE:
      return (
        <DateInput
          type={TextInputType.DATE}
          label={label}
          value={state[id]}
          error={errors[id]}
          required={required}
          minLength={minLength}
          autoComplete={autocompleteAttribute ?? 'bday'}
          handleOnChange={(e, val: any) => handleOnChange(id, val)}
        />
      );

    case formFieldEnum.TEXTAREA:
      return (
        <TextAreaInput
          label={label}
          placeholder={placeholder}
          value={state[id]}
          minLength={minLength}
          maxLength={maxLength || 140}
          autoComplete={autocompleteAttribute}
          handleOnChange={(e, message: string) => handleOnChange(id, message)}
        />
      );

    case formFieldEnum.SELECT:
      const options = (fields?.options || []).map((opt: string) => ({
        key: opt,
        value: opt
      }));

      return (
        <Select
          type={TextInputType.TEXT}
          label={label}
          options={[{ key: ' ', value: ' ' }, ...options]}
          value={state[id]}
          error={errors[id]}
          required={required}
          handleOnChange={({ target }: any) =>
            handleOnChange(id, target.value ?? '')
          }
          variant={variant}
        />
      );

    case formFieldEnum.RADIO:
    case formFieldEnum.CHECKBOX:
      return (
        <Checkbox
          type={type}
          name={formGroupId ?? 'dynamic-form-checkbox'}
          label={label}
          value={state[id]}
          checked={state[id] && state[id] !== ''}
          error={errors[id]}
          required={required}
          disclaimer={fields?.disclaimer}
          handleOnChange={(e: any, val: any) => handleOnChange(id, val)}
        />
      );

    case formFieldEnum.PASSWORD:
      return (
        <TextInput
          type={TextInputType.PASSWORD}
          label={label}
          placeholder={placeholder}
          value={state[id]}
          error={errors[id]}
          required={required}
          minLength={minLength}
          maxLength={maxLength}
          autoComplete={autocompleteAttribute}
          handleOnChange={(e, val: string) => handleOnChange(id, val)}
        />
      );

    default:
      return null;
  }
}
